/**
 * The studio's mapping table against a show style (D32), client-side: one row
 * per show-style layer — which drivers it needs, which piece types use it,
 * where the studio maps it and what is wrong with that — judged by the
 * engine's own `layerMappingStatus` / `resolveProductionConfig` on the parsed
 * documents, so the table says what `acp validate` and a production create
 * would. And the edits the table makes to the studio's document as written.
 */
import type { JsonValue } from '@norna/domain/primitives.js';
import { type LookaheadMode, ONE_SHOT_BY_DEFAULT } from '@norna/playout-engine/productionConfig.js';
import { layerMappingStatus, resolveProductionConfig } from '@norna/playout-engine/resolveConfig.js';
import type { ShowStyleConfig } from '@norna/playout-engine/showStyle.js';
import type { StudioConfig } from '@norna/playout-engine/studio.js';
import { type ConfigProblem, isJsonObject, type JsonDocument } from './config.js';

/** A mapping as the table edits it (written out whole: a row's save leaves nothing to a default). */
export type MappingEdit = {
  readonly device: string;
  readonly lookahead: LookaheadMode;
  readonly oneShot: boolean;
  readonly options: JsonDocument;
};

export type MappingRow = {
  readonly layer: string;
  readonly label: string;
  /** The drivers the layer allows; empty = any. */
  readonly deviceTypes: readonly string[];
  readonly usedBy: readonly string[];
  /** The studio's mapping of it (defaults resolved), or null. */
  readonly mapping: MappingEdit | null;
  /** The studio's devices this layer may be mapped to (of an allowed driver). */
  readonly candidates: readonly { readonly id: string; readonly type: string }[];
  /** What is wrong (unmapped while used, an incompatible device), or null. */
  readonly problem: string | null;
};

export function mappingRows(showStyle: ShowStyleConfig, studio: StudioConfig): MappingRow[] {
  const devices = Object.entries(studio.devices).map(([id, d]) => ({ id, type: d.type }));
  return layerMappingStatus(showStyle, studio).map((status) => {
    const mapped = studio.mappings[status.layer];
    return {
      layer: status.layer,
      label: status.label,
      deviceTypes: status.deviceTypes,
      usedBy: status.usedBy,
      mapping: mapped === undefined ? null : { device: mapped.device, lookahead: mapped.lookahead, oneShot: mapped.oneShot, options: { ...mapped.options } },
      candidates: devices.filter((d) => status.deviceTypes.length === 0 || status.deviceTypes.some((t) => t === d.type)),
      problem: status.problem,
    };
  });
}

/** Every problem of the pair (the layer table's and the templates' `deviceType`s), paths prefixed `showStyle.` / `studio.`. */
export function pairProblems(showStyle: ShowStyleConfig, studio: StudioConfig): ConfigProblem[] {
  const resolved = resolveProductionConfig(showStyle, studio);
  return resolved.ok ? [] : resolved.problems.map((p) => ({ path: p.path.map(String).join('.'), message: p.message }));
}

/** The studio document with `layer` mapped as `edit` (null: unmapped). Everything else stays as written. */
export function withMapping(document: JsonDocument, layer: string, edit: MappingEdit | null): JsonDocument {
  const current = document['mappings'];
  const mappings: { [key: string]: JsonValue } = isJsonObject(current) ? { ...current } : {};
  if (edit === null) delete mappings[layer];
  else {
    // Fields the table does not edit (lookaheadDepth, lookaheadMaxSearchDistance) stay as written (review finding).
    const before = mappings[layer];
    mappings[layer] = { ...(isJsonObject(before) ? before : {}), device: edit.device, lookahead: edit.lookahead, oneShot: edit.oneShot, options: { ...edit.options } };
  }
  return { ...document, mappings };
}

/** A new mapping for a layer: its first candidate device, no lookahead, one-shot as the driver defaults (video servers), no options. */
export function newMapping(row: MappingRow): MappingEdit | null {
  const device = row.candidates[0];
  if (device === undefined) return null;
  return { device: device.id, lookahead: 'none', oneShot: ONE_SHOT_BY_DEFAULT.some((t) => t === device.type), options: {} };
}
